import { useMutation, useQueryClient, type QueryKey } from '@tanstack/react-query';
import { describeError, isApiError } from '@/api/errors';
import { useToast } from '@/components/ui';

/**
 * Human command against the API. Shows honest outcome toasts: success only after the
 * server confirmed, "Not available yet" for 501, the structured problem otherwise.
 * Never retried automatically; idempotency keys are set per request by the client.
 */
export function useCommand<TVars, TData>({
  run,
  success,
  invalidate,
  onSuccess,
}: {
  run: (vars: TVars) => Promise<TData>;
  /** Toast title on confirmed success, e.g. "Recorded in the timeline. No email was sent." */
  success?: string | ((data: TData) => string);
  /** Query keys to refetch after success. */
  invalidate?: QueryKey[];
  onSuccess?: (data: TData, vars: TVars) => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation<TData, unknown, TVars>({
    mutationFn: run,
    onSuccess: async (data, vars) => {
      if (success) toast.show({ tone: 'neutral', title: typeof success === 'function' ? success(data) : success });
      await Promise.all((invalidate ?? []).map((k) => qc.invalidateQueries({ queryKey: k })));
      onSuccess?.(data, vars);
    },
    onError: (error) => {
      if (isApiError(error) && error.notImplemented) {
        toast.show({
          tone: 'accent',
          title: 'Not available yet',
          body: `${error.operationId ?? 'This command'} is in the contract but not implemented${
            error.plannedPhase ? ` (planned for ${error.plannedPhase})` : ''
          }. Nothing changed.`,
        });
        return;
      }
      const { title, body } = describeError(error);
      toast.show({
        tone: 'accent',
        title,
        body: isApiError(error) && error.requestId ? `${body} Request ${error.requestId}.` : body,
        durationMs: 10_000,
      });
    },
  });
}
